import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getMessageThreads } from '../api';

function timeAgo(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await getMessageThreads();
      setThreads(res.data.threads);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: 24 }}>
      <Helmet>
        <title>Messages — Algovex</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <h1 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
        Messages
      </h1>

      <div style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            Loading…
          </div>
        ) : threads.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
            No messages yet.
          </div>
        ) : (
          threads.map((t, idx) => (
            <div
              key={t.id}
              onClick={() => navigate(`/messages/${t.id}`)}
              style={{
                display: 'flex',
                gap: 12,
                padding: '14px 18px',
                borderBottom: idx < threads.length - 1 ? '1px solid var(--border)' : 'none',
                background: t.unread ? 'rgba(255,255,255,0.03)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                  <span style={{
                    fontWeight: t.unread ? 700 : 500,
                    fontSize: 14,
                    color: 'var(--text)',
                    flex: 1,
                    minWidth: 0,
                    wordBreak: 'break-word',
                  }}>
                    {t.subject}
                  </span>
                  {t.unread && (
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: 'var(--teal)', flexShrink: 0, marginTop: 5,
                    }} />
                  )}
                </div>
                {t.last_message && (
                  <div style={{
                    fontSize: 13,
                    color: 'var(--muted)',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {t.last_from_staff ? 'Algovex: ' : 'You: '}{t.last_message}
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, opacity: 0.7 }}>
                  {timeAgo(t.updated_at)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
